// /onboarding/shape — shape 3, «ما فترات الحجز؟» (board 25 v2 shape--3,
// board 22 §ج): the whole business is booked by period. Each row is a name,
// from, to and a price; «end ≤ start» ends the next day (booking derives
// `overnight`). «فترة مخصّصة» adds an empty row; a row is POSTed once it is
// complete and PATCHed on later edits. The ONE deposit control «العربون»
// («نسبة من المبلغ» | «مبلغ ثابت», «عند التأكيد») is written onto every
// period (R18): new periods are created with it, a change PATCHes them all.
// Finish needs one active period (R8; W12 until then).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { Chip } from '@/components/Form';
import { Skeleton } from '@/components/Skeleton';
import { normalizeDigits } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { Msg } from '../../components/widgets';
import { Wizard } from '../../components/Wizard';
import { HOURS, PERIODS, SHAPE_COMMON, SHAPE_COPY } from '../../copy';
import { finishShape } from '../../data/enroll';
import { createPeriod, listPeriods, patchPeriod, type Period, type PeriodBody, type PeriodDepositKind } from '../../data/shapeApi';
import { hhmm } from '../../preview';
import { toMinutes } from '../../state';
import { useBusinessPreview } from '../Business';
import { firstMessage, useShapeAction } from './useShapeAction';

interface Row {
  /** The saved period's id; undefined for a draft not posted yet. */
  id?: string;
  name: string;
  from: string;
  to: string;
  price: string;
}

const rowOf = (p: Period): Row => ({ id: p.id, name: p.name, from: hhmm(p.from_min), to: hhmm(p.to_min), price: String(p.price_iqd) });

/** A complete row → the wire fields; null while anything is missing. */
function bodyOf(r: Row): Required<Pick<PeriodBody, 'name' | 'from_min' | 'to_min' | 'price_iqd'>> | null {
  const from = toMinutes(r.from);
  const to = toMinutes(r.to);
  if (!r.name.trim() || from == null || to == null || !r.price.trim()) return null;
  return { name: r.name.trim(), from_min: from, to_min: to, price_iqd: Number(normalizeDigits(r.price)) };
}

export function PeriodsStep({ businessId }: { businessId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';
  const key = ['onboarding', 'periods', businessId];
  const periods = useQuery({ queryKey: key, queryFn: () => listPeriods(businessId), staleTime: Infinity, retry: 0 });
  const { act, busy, error } = useShapeAction();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [kind, setKind] = useState<PeriodDepositKind | null>(null);
  const [value, setValue] = useState('');
  const [depositError, setDepositError] = useState<string | null>(null);

  const saved = periods.data ?? [];
  useEffect(() => {
    if (rows == null && periods.data) {
      setRows(periods.data.map(rowOf));
      const withDeposit = periods.data.find((p) => p.deposit_kind);
      if (withDeposit) {
        setKind(withDeposit.deposit_kind ?? null);
        setValue(withDeposit.deposit_value != null ? String(withDeposit.deposit_value) : '');
      }
    }
  }, [periods.data, rows]);

  const { profile, preview } = useBusinessPreview('shape');
  const hasActive = saved.some((p) => p.active);
  const list = rows ?? [];

  /** The deposit as the wire carries it; null when off or incomplete. */
  const deposit = (): Pick<PeriodBody, 'deposit_kind' | 'deposit_value'> | null => {
    const v = Number(normalizeDigits(value));
    return kind && value.trim() && Number.isFinite(v) ? { deposit_kind: kind, deposit_value: v } : null;
  };

  const setSaved = (fn: (xs: Period[]) => Period[]) => qc.setQueryData<Period[]>(key, (xs) => fn(xs ?? []));

  const saveRow = (i: number, r: Row) => {
    const body = bodyOf(r);
    if (!body) return;
    const onBad = (e: Parameters<NonNullable<Parameters<typeof act>[1]>>[0]) => setRowErrors((x) => ({ ...x, [i]: firstMessage(e) }));
    if (!r.id) {
      void act(async () => {
        const p = await createPeriod(businessId, { ...body, ...(deposit() ?? {}) });
        setSaved((xs) => [...xs, p]);
        setRows((rs) => (rs ?? []).map((x, j) => (j === i ? { ...x, id: p.id } : x)));
        setRowErrors((x) => ({ ...x, [i]: '' }));
      }, onBad);
      return;
    }
    const cur = saved.find((p) => p.id === r.id);
    if (cur && cur.name === body.name && cur.from_min === body.from_min && cur.to_min === body.to_min && cur.price_iqd === body.price_iqd) return;
    void act(async () => {
      const p = await patchPeriod(businessId, r.id!, body);
      setSaved((xs) => xs.map((x) => (x.id === p.id ? p : x)));
      setRowErrors((x) => ({ ...x, [i]: '' }));
    }, onBad);
  };

  const edit = (i: number, patch: Partial<Row>) => setRows((rs) => (rs ?? []).map((r, j) => (j === i ? { ...r, ...patch } : r)));

  /** R18: the one deposit onto every saved period. */
  // Cleared (no kind, or the amount emptied): every period with a deposit loses it (deposit_kind '').
  const applyDeposit = (k: PeriodDepositKind | null, v: string) => {
    const n = Number(normalizeDigits(v));
    const clear = !k || !v.trim();
    if (!clear && !Number.isFinite(n)) return;
    // booking's own rule and sentence, before any period is touched (no half-applied loop)
    if (!clear && k === 'percent' && n > 100) {
      setDepositError('قيمة العربون غير صالحة');
      return;
    }
    const all = qc.getQueryData<Period[]>(key) ?? [];
    const targets = clear ? all.filter((p) => !!p.deposit_kind) : all.filter((p) => p.deposit_kind !== k || p.deposit_value !== n);
    setDepositError(null);
    if (targets.length === 0) return;
    void act(
      async () => {
        for (const p of targets) {
          const next = await patchPeriod(businessId, p.id, clear ? { deposit_kind: '' } : { deposit_kind: k!, deposit_value: n });
          setSaved((xs) => xs.map((x) => (x.id === next.id ? next : x)));
        }
        setDepositError(null);
      },
      (e) => setDepositError(firstMessage(e)),
    );
  };

  const loading = periods.isPending;

  return (
    <Wizard
      screen="shape"
      testId={loading ? 'onb-shape-loading' : 'onb-shape-periods'}
      state={loading ? 'loading' : busy ? 'saving' : 'default'}
      list
      question={SHAPE_COPY.business.question()}
      sub={SHAPE_COPY.business.sub()}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} disabled={!hasActive}>
          {SHAPE_COMMON.finish}
        </PrimaryAction>
      }
      onSubmit={() => {
        if (hasActive) finishShape(navigate);
      }}
      error={periods.isError ? { kind: 'network', onRetry: () => void periods.refetch() } : error}
      panel={{ phase: 'preview', profile, preview }}
    >
      {loading ? (
        <Skeleton height={120} />
      ) : (
        <>
          <div className="onb-listcard" data-testid="onb-periods">
            {list.map((r, i) => (
              <div key={i} className="onb-row onb-period-row" data-testid="onb-period-row">
                <input className="onb-entity-name" aria-label={SHAPE_COMMON.name} data-testid={`onb-period-name-${i}`} value={r.name} onChange={(e) => edit(i, { name: e.target.value })} onBlur={() => saveRow(i, list[i])} />
                <input type="time" className="onb-time ltr" dir="ltr" step={900} aria-label={HOURS.from} data-testid={`onb-period-from-${i}`} value={r.from} onChange={(e) => edit(i, { from: e.target.value })} onBlur={() => saveRow(i, list[i])} />
                <span className="onb-day-dash" aria-hidden="true">
                  –
                </span>
                <input type="time" className="onb-time ltr" dir="ltr" step={900} aria-label={HOURS.to} data-testid={`onb-period-to-${i}`} value={r.to} onChange={(e) => edit(i, { to: e.target.value })} onBlur={() => saveRow(i, list[i])} />
                <input
                  className="onb-entity-price ltr"
                  dir="ltr"
                  inputMode="numeric"
                  aria-label={SHAPE_COMMON.price}
                  data-testid={`onb-period-price-${i}`}
                  value={r.price}
                  onChange={(e) => edit(i, { price: normalizeDigits(e.target.value) })}
                  onBlur={() => saveRow(i, list[i])}
                />
                {rowErrors[i] && <Msg testId={`onb-period-error-${i}`}>{rowErrors[i]}</Msg>}
              </div>
            ))}
            <button type="button" className="onb-entity-add" onClick={() => setRows([...list, { name: '', from: '', to: '', price: '' }])}>
              <Plus aria-hidden="true" />
              {PERIODS.custom}
            </button>
          </div>
          {!hasActive && (
            <Msg tone="info" testId="onb-shape-min-one">
              {SHAPE_COMMON.atLeastOne}
            </Msg>
          )}

          <div className="onb-entity-section" data-testid="onb-deposit">
            <div className="onb-entity-section-title">{PERIODS.deposit}</div>
            <div className="onb-sitting-opts">
              {(['percent', 'fixed'] as const).map((k) => (
                <Chip
                  key={k}
                  selected={kind === k}
                  onClick={() => {
                    // the selected chip again = no deposit
                    const next = kind === k ? null : k;
                    setKind(next);
                    applyDeposit(next, value);
                  }}
                >
                  {k === 'percent' ? PERIODS.percent : PERIODS.fixed}
                </Chip>
              ))}
            </div>
            <input
              className="onb-entity-price ltr"
              dir="ltr"
              inputMode="numeric"
              aria-label={PERIODS.deposit}
              data-testid="onb-deposit-value"
              value={value}
              onChange={(e) => setValue(normalizeDigits(e.target.value))}
              onBlur={(e) => applyDeposit(kind, normalizeDigits(e.target.value))}
            />
            <span className="onb-row-sub">{PERIODS.onConfirm}</span>
            {depositError && <Msg testId="onb-deposit-error">{depositError}</Msg>}
          </div>
        </>
      )}
    </Wizard>
  );
}
